/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HeaderModule
 */

import {
    Component,
    Input,
    OnDestroy,
    Type,
} from '@angular/core';

import {
    Observable,
    Subject,
} from 'rxjs';

import { ConnectedPosition } from '@angular/cdk/overlay';
import { L10nService } from '@vmw/ngx-vip';
import { DialogService } from 'dialog-service';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';
import { GenerateSsoTokenDialogComponent } from '../../../generate-sso-token-dialog';
import { AppAboutModalComponent } from '../../../app-about-modal';
import { AviMainHeaderUserMenuService } from './avi-main-header-user-menu.service';
import * as l10n from './avi-main-header-user-menu.l10n';
import './avi-main-header-user-menu.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const GENERATE_SSO_TOKEN_DIALOG_ID = 'GENERATE_SSO_TOKEN_DIALOG_ID';
const APP_ABOUT_DIALOG_ID = 'APP_ABOUT_DIALOG_ID';

/**
 * @description Component for the main header user dropdown.
 * @author alextsg
 */
@Component({
    selector: 'avi-main-header-user-menu',
    templateUrl: './avi-main-header-user-menu.component.html',
    providers: [AviMainHeaderUserMenuService],
})
export class AviMainHeaderUserMenuComponent implements OnDestroy {
    @Input()
    public username: string;

    @Input()
    public fullName: string;

    @Input()
    public email: string;

    @Input()
    public lastLogin: string;

    @Input()
    public customerPortalUrl: string;

    /**
     * User dropdown/tooltip position strategy.
     */
    public menuPositions: ConnectedPosition[] = [
        {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 5,
            offsetX: 5,
        },
    ];

    /**
     * Controls closing of the dropdown menu.
     */
    public userMenuControl$: Observable<boolean>;

    public l10nKeys = l10nKeys;

    /**
     * Subject to control user menu.
     */
    private userMenuControlSubject = new Subject<boolean>();

    constructor(
        private readonly userMenuService: AviMainHeaderUserMenuService,
        private readonly dialogService: DialogService,
        l10nService: L10nService,
        private readonly auth: Auth,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.userMenuControl$ = this.userMenuControlSubject.asObservable();
    }

    /**
     * @override
     * Close the dropdown menu and any open dialogs.
     */
    public ngOnDestroy(): void {
        this.userMenuControlSubject.next(false);

        if (this.userMenuService.isMyAccountModalOpen()) {
            this.userMenuService.closeMyAccountModal();
        }

        if (this.dialogService.has(GENERATE_SSO_TOKEN_DIALOG_ID)) {
            this.dialogService.remove(GENERATE_SSO_TOKEN_DIALOG_ID);
        }

        if (this.dialogService.has(APP_ABOUT_DIALOG_ID)) {
            this.dialogService.remove(APP_ABOUT_DIALOG_ID);
        }
    }

    /**
     * Open "My Account" modal.
     */
    public handleOpenMyAccountModal(): void {
        this.closeMenu();
        this.userMenuService.openMyAccountModal();
    }

    /**
     * Open "Generate SSO Token" dialog.
     */
    public handleOpenGenerateTokenDialog(): void {
        this.closeMenu();

        this.dialogService.add({
            id: GENERATE_SSO_TOKEN_DIALOG_ID,
            component: GenerateSsoTokenDialogComponent as Type<Component>,
            componentProps: {
                onClose: () => this.dialogService.remove(GENERATE_SSO_TOKEN_DIALOG_ID),
            },
        });
    }

    /**
     * Open "About Avi" modal.
     */
    public handleOpenAboutDialog(): void {
        this.closeMenu();

        this.dialogService.add({
            id: APP_ABOUT_DIALOG_ID,
            component: AppAboutModalComponent as Type<Component>,
            componentProps: {
                onClose: () => this.dialogService.remove(APP_ABOUT_DIALOG_ID),
            },
        });
    }

    /**
     * Open Avi documentation in a new tab.
     */
    public goToAviDocumentation(): void {
        this.closeMenu();

        window.open('/swagger/', '_blank');
    }

    /**
     * Open Customer Portal link in a new tab.
     */
    public goToCustomerPortal(): void {
        this.closeMenu();

        window.open(this.customerPortalUrl, '_blank');
    }

    /**
     * Log out of the application.
     */
    public handleLogout(): void {
        this.auth.logout(true);
    }

    /**
     * Close dropdown menu.
     */
    private closeMenu(): void {
        this.userMenuControlSubject.next(false);
    }
}
